import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BsFillPlayFill, BsPauseFill, BsSkipStartFill, BsSkipEndFill } from 'react-icons/bs';
import { TbArrowsDiagonal } from "react-icons/tb";
import { usePlayer } from './PlayerContext';
import './Footer.css';

// Spotify API imports (for /q page)
import { isSessionUnlocked, getAccessToken } from './Queue/spotifyAuth';

// Pages that have music/Spotify integration
const MUSIC_PAGES = ['/', '/education'];

const Footer = () => {
    const [profile, setProfile] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const {
        currentTrack,
        currentTrackInfo,
        isPlaying,
        togglePlay,
        nextTrack,
        prevTrack,
        playFirst,
        playlist,
        currentIndex
    } = usePlayer();

    // Spotify state (for /q page)
    const [spotifyTrack, setSpotifyTrack] = useState(null);
    const [spotifyPlaying, setSpotifyPlaying] = useState(false);

    // Check if on queue page with active session
    const isOnQueuePage = location.pathname === '/q';
    const isQueueSessionActive = isOnQueuePage && isSessionUnlocked();

    // Check if current page has music
    const isOnMusicPage = MUSIC_PAGES.includes(location.pathname);

    // Load profile
    useEffect(() => {
        fetch('/profile.json')
            .then(res => res.json())
            .then(data => setProfile(data))
            .catch(err => console.error('Error loading profile for footer:', err));
    }, []);

    // Fetch Spotify currently playing (for /q page)
    const fetchSpotifyNowPlaying = useCallback(async () => {
        if (!isQueueSessionActive) return;

        try {
            const token = await getAccessToken();
            if (!token) return;

            const response = await fetch('https://api.spotify.com/v1/me/player/currently-playing', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.status === 204 || !response.ok) {
                setSpotifyTrack(null);
                setSpotifyPlaying(false);
                return;
            }

            const data = await response.json();
            setSpotifyTrack(data.item);
            setSpotifyPlaying(data.is_playing);
        } catch (err) {
            console.error('Failed to fetch Spotify now playing:', err);
        }
    }, [isQueueSessionActive]);

    // Poll Spotify every 5 seconds when on /q
    useEffect(() => {
        if (isQueueSessionActive) {
            fetchSpotifyNowPlaying();
            const interval = setInterval(fetchSpotifyNowPlaying, 5000);
            return () => clearInterval(interval);
        }
    }, [isQueueSessionActive, fetchSpotifyNowPlaying]);

    // Spotify playback controls
    const spotifyControl = async (action) => {
        try {
            const token = await getAccessToken();
            if (!token) return;

            let endpoint = '';
            let method = 'POST';

            switch (action) {
                case 'play':
                    endpoint = '/me/player/play';
                    method = 'PUT';
                    break;
                case 'pause':
                    endpoint = '/me/player/pause';
                    method = 'PUT';
                    break;
                case 'next':
                    endpoint = '/me/player/next';
                    break;
                case 'previous':
                    endpoint = '/me/player/previous';
                    break;
                default:
                    return;
            }

            await fetch(`https://api.spotify.com/v1${endpoint}`, {
                method,
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // Refresh now playing after action
            setTimeout(fetchSpotifyNowPlaying, 300);
        } catch (err) {
            console.error('Spotify control failed:', err);
        }
    };

    const handleFullScreen = () => {
        if (document.fullscreenElement) {
            document.exitFullscreen();
        } else {
            document.documentElement.requestFullscreen();
        }
    };

    // Handle play button click
    const handlePlayClick = () => {
        if (isQueueSessionActive) {
            spotifyControl(spotifyPlaying ? 'pause' : 'play');
            return;
        }

        if (!isOnMusicPage) return;

        if (currentTrack) {
            togglePlay();
        } else {
            playFirst();
        }
    };

    // Handle prev/next
    const handlePrev = () => {
        if (isQueueSessionActive) {
            spotifyControl('previous');
            return;
        }
        if (!isOnMusicPage) return;
        if (canGoPrev) prevTrack();
    };

    const handleNext = () => {
        if (isQueueSessionActive) {
            spotifyControl('next');
            return;
        }
        if (!isOnMusicPage) return;
        if (canGoNext) nextTrack();
    };

    // Check if prev/next should be enabled
    const canGoPrev = isOnMusicPage && playlist.length > 0 && currentIndex > 0;
    const canGoNext = isOnMusicPage && playlist.length > 0 && currentIndex < playlist.length - 1;

    // Determine what to display
    let displayTitle, displayArtist, displayThumbnail, showPlayingState, controlsEnabled;

    if (isQueueSessionActive && spotifyTrack) {
        // Spotify mode on /q
        displayTitle = spotifyTrack.name;
        displayArtist = spotifyTrack.artists?.map(a => a.name).join(', ');
        displayThumbnail = spotifyTrack.album?.images[2]?.url || spotifyTrack.album?.images[0]?.url;
        showPlayingState = spotifyPlaying;
        controlsEnabled = true;
    } else if (isOnMusicPage && currentTrack) {
        // Local player mode
        displayTitle = currentTrackInfo.title;
        displayArtist = currentTrackInfo.artist;
        displayThumbnail = currentTrackInfo.thumbnail;
        showPlayingState = isPlaying;
        controlsEnabled = true;
    } else {
        // Default profile mode
        displayTitle = profile?.name ? "Hello!" : "...";
        displayArtist = profile?.name || "Divyesh Thirukonda";
        displayThumbnail = profile?.photo;
        showPlayingState = false;
        controlsEnabled = false;
    }

    const showTrackInfo = (isQueueSessionActive && spotifyTrack) || (isOnMusicPage && currentTrack);

    return (
        <div className="footer">
            {/* Now Playing (Left) */}
            <div className="now-playing" onClick={() => !showTrackInfo && navigate('/')} style={{ cursor: showTrackInfo ? 'default' : 'pointer' }}>
                <div className="album-art">
                    {displayThumbnail ? (
                        <img src={displayThumbnail} alt="Now Playing" />
                    ) : (
                        <span>DT</span>
                    )}
                </div>
                <div className="track-info">
                    <div className="track-name">{displayTitle}</div>
                    <div className="artist-name">{displayArtist}</div>
                </div>
            </div>

            {/* Player Controls (Center) */}
            <div className="player-controls">
                <div className="control-buttons">
                    <button
                        className={`control-btn ${!controlsEnabled ? 'disabled' : ''}`}
                        aria-label="Previous"
                        onClick={handlePrev}
                        disabled={!controlsEnabled && !canGoPrev}
                    >
                        <BsSkipStartFill size={20} />
                    </button>
                    <button
                        className={`control-btn play-pause ${!controlsEnabled ? 'disabled' : ''}`}
                        aria-label={showPlayingState ? "Pause" : "Play"}
                        onClick={handlePlayClick}
                        disabled={!controlsEnabled}
                    >
                        {showPlayingState ? <BsPauseFill size={20} /> : <BsFillPlayFill size={20} style={{ marginLeft: '2px' }} />}
                    </button>
                    <button
                        className={`control-btn ${!controlsEnabled ? 'disabled' : ''}`}
                        aria-label="Next"
                        onClick={handleNext}
                        disabled={!controlsEnabled && !canGoNext}
                    >
                        <BsSkipEndFill size={20} />
                    </button>
                </div>
            </div>

            {/* Right Controls - just fullscreen */}
            <div className="volume-controls">
                <button className="control-btn" aria-label="Full screen" onClick={handleFullScreen}>
                    <TbArrowsDiagonal size={16} />
                </button>
            </div>
        </div>
    );
};

export default Footer;
